/**
 * Where each node ended up on screen, published once per frame by whichever
 * renderer just painted.
 *
 * This exists because the text overlay used to know how the camera worked. It
 * computed `v.x + at.x * v.k` inline and took each caption's outward direction
 * from `atan2(at.y, at.x)` — both perfectly correct, and both statements that the
 * projection is a 2D affine transform. With a second view that projects through a
 * perspective camera, either the overlay grows a branch per view or the knowledge
 * moves somewhere both can write it. It moved here.
 *
 * The same pattern, and the same reasoning, as `growth.ts`: the renderer already
 * resolved this to paint with, so it publishes the answer rather than leaving a
 * second consumer to re-derive it and drift.
 *
 * Read only inside an `onAfterDraw` callback. Anywhere else it is last frame's
 * camera, which is precisely the slip `frameBus.ts` exists to prevent.
 */

export interface Placed {
  /** Screen pixels. */
  x: number
  y: number
  /**
   * Unit vector pointing away from the seed, in SCREEN space.
   *
   * The overlay places a caption along its node's own spoke so it always looks
   * like it belongs to that strand. In 2D that direction is the node's world
   * angle, which happens to survive the transform. Under perspective it does not:
   * a spoke pointing away from the seed in the world can point any direction on
   * screen once the camera is off-axis, so the direction has to be computed after
   * projection rather than before it.
   */
  ux: number
  uy: number
  /** Pixels per world unit at this node. The 2D zoom, or the perspective scale. */
  s: number
  /** How far in front of the eye, for culling and ordering. Zero in 2D. */
  z: number
  /** False when the node is off-stage or behind the camera. */
  on: boolean
}

const OFF: Placed = { x: 0, y: 0, ux: 1, uy: 0, s: 1, z: 0, on: false }

export const screen = {
  /** Placement for one node id, valid for the frame just painted. */
  place: (_id: string): Placed => OFF,
  /** The seed's screen position — the one fixed obstacle the overlay dodges. */
  originX: 0,
  originY: 0,
  /** Screen radius of the seed orb, so the overlay can keep clear of it. */
  orbR: 0,
  /**
   * The seed caption's measured INK box (its title's widest line by its
   * height), screen px — written by the caption layer,
   * read by the flat renderer, which keeps strands out of an ellipse fitted to
   * it so the title sits on clear field at every zoom (D350). A caption keeps
   * its type size while the graph zooms, so a clear field in world units alone
   * is too small zoomed out. One frame stale, which the renderer tolerates.
   */
  seedW: 0,
  seedH: 0,
  /** The seed caption's camera scale (`seedScale`), so the rim's air scales
   *  with the words it stands off. */
  seedScale: 1,
  /**
   * A zoom-like scalar in [0, ~2], for the overlay's "only load-bearing captions
   * survive when pulled back" gate. The 2D view's own `k`; in 3D, the perspective
   * scale at the seed normalised to the same range, so one threshold works for
   * both without either view needing to know about the other.
   */
  zoom: 1,
  /** Which view painted. The overlay needs it for one decision: in 3D a caption
   *  may be occluded by geometry in front of it, so depth joins priority. */
  mode: '2d' as '2d' | '3d',
  /**
   * Bumped by whichever renderer painted, on every frame in which a node's screen
   * position could have moved: the camera changed, the layout is still easing, the
   * graph's revision moved, the timeline scrubbed, or the stage was resized. Left
   * alone otherwise — and "otherwise" is most frames. The canvas never sleeps (the
   * seed breathes, signals travel), but the caption layer and the minimap only
   * care where things ARE, so they read this to skip the frames where nothing
   * they draw has changed. See finding 6 in docs/performance-audit.md.
   */
  frame: 0,
  /**
   * True while something the overlay draws is mid-animation without moving: a
   * strand still growing (its caption fades up on touchdown), or a replay in
   * progress. Read with `frame`; a caption tick that skipped these frames would
   * freeze a fade halfway.
   */
  animating: false,
}
